/**
 * The bot list and the currently selected bot.
 *
 * Every page is scoped to one bot, so the selection lives above the router. The
 * choice is remembered in `localStorage`: staff work on one site for a stretch
 * at a time, and resetting to the first bot on every reload is a papercut.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { api } from '../api/client';
import type { BotSummary } from '../api/types';

const SELECTED_BOT_KEY = 'formachat.selectedBot';

/** Switcher value for the cross-site overview rather than one bot. */
export const ALL_SITES = '__all__';

interface BotsState {
  bots: BotSummary[];
  /** Null while "All websites" is chosen; every bot-scoped page needs one. */
  selected: BotSummary | null;
  allSelected: boolean;
  loading: boolean;
  error: string | null;
  select: (slug: string) => void;
  reload: () => Promise<void>;
}

const BotsContext = createContext<BotsState | null>(null);

export function BotsProvider({ children }: { children: ReactNode }): JSX.Element {
  const [bots, setBots] = useState<BotSummary[]>([]);
  const [slug, setSlug] = useState<string | null>(() =>
    localStorage.getItem(SELECTED_BOT_KEY),
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await api.listBots();
      setBots(list);
      setSlug((current) => {
        // Keep the remembered choice only if it still exists.
        if (current === ALL_SITES) return current;
        if (current && list.some((bot) => bot.slug === current)) return current;
        return list[0]?.slug ?? null;
      });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not load bots');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const select = useCallback((next: string) => {
    setSlug(next);
    localStorage.setItem(SELECTED_BOT_KEY, next);
  }, []);

  const selected = useMemo(
    () => bots.find((bot) => bot.slug === slug) ?? null,
    [bots, slug],
  );

  const allSelected = slug === ALL_SITES;

  const value = useMemo(
    () => ({ bots, selected, allSelected, loading, error, select, reload }),
    [bots, selected, allSelected, loading, error, select, reload],
  );

  return <BotsContext.Provider value={value}>{children}</BotsContext.Provider>;
}

/** Read the bot selection. Throws when used outside `BotsProvider`. */
export function useBots(): BotsState {
  const context = useContext(BotsContext);
  if (!context) throw new Error('useBots must be used inside a BotsProvider');
  return context;
}
